/**
 * Attempts to parse a date string using locale or user preference.
 * @param dateString The date string to parse.
 * @param locale Optional. 'en-US' (MM/DD/YYYY) or 'en-GB' (DD/MM/YYYY) or 'de-DE' (DD.MM.YYYY), etc.
 */
function parseDate(dateString, locale = 'en-GB') {
    const formats = [
        // YYYY-MM-DD or YYYY/MM/DD or YYYY.MM.DD
        { regex: /^\d{4}[-\/.]\d{2}[-\/.]\d{2}$/, parse: (str) => {
            const [y, m, d] = str.split(/[-\/.]/).map((part) => Number(part));
            return new Date(y, m - 1, d);
        }},
        // DD.MM.YYYY or MM.DD.YYYY (locale sensitive)
        { regex: /^\d{2}\.\d{2}\.\d{4}$/, parse: (str) => {
            let [a, b, y] = str.split('.');
            const year = Number(y);
            const first = Number(a);
            const second = Number(b);
            if (locale === 'de-DE' || locale === 'en-GB') {
                // DD.MM.YYYY
                return new Date(year, second - 1, first);
            } else if (locale === 'en-US') {
                // MM.DD.YYYY
                return new Date(year, first - 1, second);
            }
            // Default to DD.MM.YYYY
            return new Date(year, second - 1, first);
        }},
        // MM/DD/YYYY or DD/MM/YYYY (locale sensitive)
        { regex: /^\d{2}\/\d{2}\/\d{4}$/, parse: (str) => {
            let [a, b, y] = str.split('/');
            const year = Number(y);
            const first = Number(a);
            const second = Number(b);
            if (locale === 'en-US') {
                // MM/DD/YYYY
                return new Date(year, first - 1, second);
            } else {
                // DD/MM/YYYY
                return new Date(year, second - 1, first);
            }
        }},
        // DD-MM-YYYY or MM-DD-YYYY (locale sensitive)
        { regex: /^\d{2}-\d{2}-\d{4}$/, parse: (str) => {
            let [a, b, y] = str.split('-');
            const year = Number(y);
            const first = Number(a);
            const second = Number(b);
            if (locale === 'en-US') {
                // MM-DD-YYYY
                return new Date(year, first - 1, second);
            } else {
                // DD-MM-YYYY
                return new Date(year, second - 1, first);
            }
        }},
        // DD-MM-YY
        { regex: /^\d{2}-\d{2}-\d{2}$/, parse: (str) => {
            let [d, m, y] = str.split('-');
            const year = Number('20' + y);
            const month = Number(m);
            const day = Number(d);
            return new Date(year, month - 1, day);
        }},
        // MM/DD/YY
        { regex: /^\d{2}\/\d{2}\/\d{2}$/, parse: (str) => {
            let [m, d, y] = str.split('/');
            const year = Number('20' + y);
            const month = Number(m);
            const day = Number(d);
            return new Date(year, month - 1, day);
        }},
        // ISO with time: 2025-12-23T00:00:00 or 2025-12-23T00:00:00Z
        { regex: /^\d{4}-\d{2}-\d{2}T.*$/, parse: (str) => new Date(str) },
        // Single digit day/month: D/M/YYYY or D-M-YYYY or D.M.YYYY (locale sensitive)
        { regex: /^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4}$/, parse: (str) => {
            let sep = str.includes('/') ? '/' : (str.includes('-') ? '-' : '.');
            let [a, b, y] = str.split(sep);
            const year = Number(y);
            const first = Number(a);
            const second = Number(b);
            if (locale === 'en-US') {
                // M/D/YYYY
                return new Date(year, first - 1, second);
            } else {
                // D/M/YYYY
                return new Date(year, second - 1, first);
            }
        }},
    ];

    for (let {regex, parse} of formats) {
        if (regex.test(dateString)) {
            let date = parse(dateString);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }
    }
    return null;
}

/**
 * Formats a date string to YYYY-MM-DD, using locale if needed.
 * @param dateStr The date string to format.
 * @param locale Optional. 'en-US', 'en-GB', etc.
 */
export function checkAndFormatDate(dateStr, locale = (typeof navigator !== 'undefined' ? navigator.language : 'en-US')) {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (regex.test(dateStr)) {
        return dateStr;
    }
    const date = parseDate(dateStr, locale);
    if (!date || isNaN(date.getTime())) {
        return null;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}